import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * The Fidèle: keeps the introductory gift honest in the bag, wherever the bag was filled.
 *
 * `snippets/stack-gift-guard.liquid` renders, in the cart drawer and on the cart page:
 * - <stack-gift-guard> when the gift line (line property `_stack_gift`, or the gift product named
 *   in Theme settings > The Fidèle) is in the bag but the bag's subtotal, without that gift, is
 *   under the gift's minimum (Theme settings > Stack rewards): it takes that one line out
 *   (data-quantity="0"), or back to one gift when there are more (data-quantity="1"), through
 *   Horizon's own cart update path. <stack-gift-notice> then tells the shopper why, once.
 * - <stack-gift-sync> when the bag reaches the minimum with no gift line, checkout is open and the
 *   offer has not ended: it adds one gift line. If the shopper then takes the gift out herself, it
 *   is not added again to the same bag (the bag's token is remembered for this browser session).
 *
 * It never applies a discount and never shows a price. See STACK_BUILDER_README.md at the theme root.
 */

const NOTICE_KEY = 'fidele-stack-gift-removed';
const ADDED_KEY = 'fidele-stack-gift-added';
const EVENT_SOURCE = 'stack-gift-guard';
const SYNC_SOURCE = 'stack-gift-sync';

/** Line keys already handled on this page, so a failed request can never loop. */
const handled = new Set();

function routes() {
  /** @type {any} */
  const theme = typeof Theme !== 'undefined' ? Theme : null;
  return theme?.routes || {};
}

/** A route with exactly one ".js" at the end (Horizon's cart_add_url already carries it). */
function js(route) {
  return `${String(route).replace(/\.js$/, '')}.js`;
}

function changeUrl() {
  return js(routes().cart_change_url || '/cart/change');
}

function addUrl() {
  return js(routes().cart_add_url || '/cart/add');
}

function cartUrl() {
  return js(routes().cart_url || '/cart');
}

/** The token of the bag the sync last added a gift to, or ''. */
function readAdded() {
  try {
    return window.sessionStorage.getItem(ADDED_KEY) || '';
  } catch {
    return '';
  }
}

/** @param {string} token */
function writeAdded(token) {
  try {
    if (token) window.sessionStorage.setItem(ADDED_KEY, token);
    else window.sessionStorage.removeItem(ADDED_KEY);
  } catch {
    // Storage is blocked: the worst case is that a gift she took out comes back once.
  }
}

function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/** @param {{ gift: string, amount: string }} value */
function writeNotice(value) {
  try {
    window.sessionStorage.setItem(NOTICE_KEY, JSON.stringify(value));
  } catch {
    // Storage is blocked: the bag still updates, only the explanation is lost.
  }
}

/** @returns {{ gift: string, amount: string } | null} */
function takeNotice() {
  try {
    const raw = window.sessionStorage.getItem(NOTICE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(NOTICE_KEY);
    const value = JSON.parse(raw);
    return value && typeof value.gift === 'string' && typeof value.amount === 'string' ? value : null;
  } catch {
    return null;
  }
}

class StackGiftGuard extends HTMLElement {
  connectedCallback() {
    const key = this.dataset.lineKey;
    const quantity = this.dataset.quantity === '1' ? 1 : 0;
    // Keyed by line and target, so a gift trimmed to one can still be taken out later on the same page.
    if (!key || handled.has(`${key}:${quantity}`)) return;
    handled.add(`${key}:${quantity}`);

    const gift = this.dataset.giftTitle || '';
    const amount = this.dataset.threshold || '';
    // A second gift line goes without a notice (DECISIONS row 62: one gift per bag, no discount caps it now).
    const silent = this.hasAttribute('data-silent');
    // Let the render that inserted this element settle first.
    window.setTimeout(() => removeGift(key, quantity === 0 && !silent ? { gift, amount } : null, quantity), 0);
  }
}

/**
 * @param {string} key - The cart line key of the gift.
 * @param {{ gift: string, amount: string } | null} notice - Shown after a removal; none for a trim to one.
 * @param {number} quantity - 0 takes the gift out; 1 trims extra gifts back to one.
 */
async function removeGift(key, notice, quantity = 0) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity === 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();
    if (!response.ok || cart?.status || cart?.errors) {
      deferred.reject(new Error(cart?.description || cart?.message || 'The gift could not be removed.'));
      return;
    }

    // Written before the bag re-renders, so the notice element in the new markup finds it.
    if (notice) {
      writeNotice(notice);
      // The guard took it out, not the shopper, so the sync may add it again if the bag qualifies.
      writeAdded('');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections ?? undefined,
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: EVENT_SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error('The gift could not be removed.'));
  }
}

let syncBusy = false;

class StackGiftSync extends HTMLElement {
  connectedCallback() {
    const variantId = Number(this.dataset.variantId);
    const token = this.dataset.cartToken || 'bag';
    if (!variantId || syncBusy) return;
    // The sync added a gift to this bag before and it is gone: she took it out herself.
    if (readAdded() === token) return;

    syncBusy = true;
    window.setTimeout(() => {
      addGift(variantId, token).finally(() => {
        syncBusy = false;
      });
    }, 0);
  }
}

/**
 * Adds one gift line through Horizon's cart update path, so the drawer, the cart page and the
 * cart count re-render the usual way.
 * @param {number} variantId
 * @param {string} token
 */
async function addGift(variantId, token) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _stack_gift: '1' } }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok || result?.status) {
      deferred.reject(new Error(result?.description || result?.message || 'The gift could not be added.'));
      return;
    }

    writeAdded(token);

    const cartResponse = await fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: result.sections ?? undefined,
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: SYNC_SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error('The gift could not be added.'));
  }
}

/**
 * The snippet gives this element an id built from the bag's count and subtotal, and Horizon's morph
 * matches elements by id, so every changed bag inserts a fresh element and this runs again.
 */
class StackGiftNotice extends HTMLElement {
  connectedCallback() {
    const notice = takeNotice();
    if (!notice) return;

    const template = this.dataset.template || '';
    const text = template.split('[gift]').join(notice.gift).split('[amount]').join(notice.amount);
    if (!text) return;

    this.textContent = text;
    this.hidden = false;
  }
}

if (!customElements.get('stack-gift-guard')) customElements.define('stack-gift-guard', StackGiftGuard);
if (!customElements.get('stack-gift-notice')) customElements.define('stack-gift-notice', StackGiftNotice);
if (!customElements.get('stack-gift-sync')) customElements.define('stack-gift-sync', StackGiftSync);
